import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, PageHeader } from 'antd';

import * as S from './CompanyList.styles';
import { useCompanySlice } from '../../slice';

import { generatePath, RouteComponentProps } from 'react-router';
import { RouteConstants } from 'app/routes';
import CompanyStatusTag from 'app/components/CompanyStatusTag/CompanyStatusTag';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { companyListTableColEnum, ICompanyInList } from '../../slice/types';
import { selectCompanyListState } from '../../slice/selectors';
import { CONFIG } from 'utils/constants';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { PermissionsEnum } from 'app/containers/User/slice/types';
import { ColumnsType } from 'antd/lib/table';
import { StyledTable } from 'app/components/StyledTable/StyledTable';

interface IProps extends RouteComponentProps {}

const CompanyList = ({ history }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { actions } = useCompanySlice();

  const { loading, companiesArr } = useSelector(selectCompanyListState);
  const envId = useSelector(selectSelectedEnvId);

  useEffect(() => {
    if (envId) {
      dispatch(actions.getCompanies(envId));
    }
  }, [envId, dispatch, actions]);

  const columns: ColumnsType<ICompanyInList> = [
    {
      title: t('Company.List.Table.CompanyName', 'Company name'),
      dataIndex: companyListTableColEnum.name,
      key: companyListTableColEnum.name,
      sorter: {
        compare: (a: ICompanyInList, b: ICompanyInList) => {
          return a.name.localeCompare(b.name);
        },
      },
    },
    {
      title: t('Company.List.Table.CompanyCode', 'Company code'),
      dataIndex: companyListTableColEnum.companyCode,
      key: companyListTableColEnum.companyCode,
      sorter: {
        compare: (a: ICompanyInList, b: ICompanyInList) => {
          return a.companyCode?.localeCompare(b.companyCode);
        },
      },
    },
    {
      title: t('Company.List.Table.city', 'City'),
      dataIndex: companyListTableColEnum.city,
      key: companyListTableColEnum.city,
      sorter: {
        compare: (a: ICompanyInList, b: ICompanyInList) => {
          return a.name.localeCompare(b.name);
        },
      },
    },
    {
      title: t('Company.List.Table.Status', 'Status'),
      dataIndex: companyListTableColEnum.accepted,
      key: companyListTableColEnum.accepted,
      render: (_: unknown, company) => (
        <CompanyStatusTag status={company.accepted} owner={company.owner} />
      ),
      sorter: {
        compare: (a: ICompanyInList, b: ICompanyInList) => {
          let Dx = a.owner === b.owner ? 0 : a.owner ? -1 : 1;
          if (Dx === 0) {
            Dx = a.accepted === b.accepted ? 0 : a.accepted ? -1 : 1;
          }
          return Dx;
        },
      },
    },
  ];

  const openCompanyForm = () => {
    dispatch(actions.setCompanyFormType({ type: 'create' }));
    dispatch(actions.toggleCompanyForm(true));
  };

  const openCompanyDetails = (companyId: number) => () => {
    if (envId) {
      history.push(
        generatePath(RouteConstants.company.details, {
          envId: Number(envId),
          companyId,
        }),
      );
    }
  };

  if (!envId) {
    return null;
  }

  return (
    <S.CompanyListWrapper>
      <PageHeader
        className="page-header"
        title={t('Company.List.Title', 'Companies')}
        extra={[
          <PermissionsWrapper
            key="1"
            requiredEditPermissions={[PermissionsEnum.companyAdministration]}
          >
            <Button
              data-testid="header-create-company"
              type="primary"
              onClick={openCompanyForm}
            >
              {t('Company.List.CreateCompany', 'Create Company')}
            </Button>
          </PermissionsWrapper>,
        ]}
      />
      <S.ContentWrapper>
        <Alert
          message={t('Company.List.EnvIdMessage', {
            defaultValue:
              'If you want to add a company owned by another person, send him your Environmental Identification Number (ID): {{id}}',
            id: envId,
          })}
          type="info"
        />
        {loading ? (
          <S.Spinner tip={t('Global.Loading', 'Loading...')} size="large" />
        ) : companiesArr.length ? (
          <StyledTable
            rowKey="id"
            style={{ marginTop: '30px' }}
            dataSource={companiesArr}
            columns={columns}
            pagination={{
              showTotal: () =>
                t('Company.List.Table.TotalPages', {
                  defaultValue: 'Total: {{total}}',
                  total: companiesArr.length,
                }),
              total: companiesArr.length,
              pageSize: CONFIG.TABLE_PAGE_SIZE,
            }}
            onRow={company => {
              return {
                onClick: openCompanyDetails(company.id),
              };
            }}
          />
        ) : (
          <S.EmptyStateWrapper>
            <S.InfoIcon />
            <S.EmptyStateText>
              {t('Company.List.NoCompany', 'You currently have no company')}
            </S.EmptyStateText>
            <Button type="primary" onClick={openCompanyForm}>
              {t('Company.List.CreateCompany', 'Create Company')}
            </Button>
          </S.EmptyStateWrapper>
        )}
      </S.ContentWrapper>
    </S.CompanyListWrapper>
  );
};

export default CompanyList;
